import { defineStore } from 'pinia'
import { ref } from 'vue'
import { fetchAuditLogsRequest } from '../services/auditService.js'
import { extractArrayEnvelope, parsePaginationEnvelope, toPositiveIntOr } from '../utils/contracts.js'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 50

const DEFAULT_FILTERS = {
  action: '',
  entity_type: '',
  organization_id: '',
  search: '',
}

export const useAuditStore = defineStore('audit', () => {
  const logs = ref([])
  const loading = ref(false)
  const error = ref(null)

  const page = ref(DEFAULT_PAGE)
  const pageSize = ref(DEFAULT_PAGE_SIZE)
  const total = ref(0)

  const filters = ref({ ...DEFAULT_FILTERS })
  const lastSuccessfulFetchAt = ref('')
  const degradedWarning = ref('')
  const partialDataWarning = ref('')
  const backendPaginationAvailable = ref(true)
  let requestToken = 0

  const fetchAuditLogs = async (options = {}) => {
    const token = ++requestToken
    loading.value = true
    error.value = null
    degradedWarning.value = ''
    partialDataWarning.value = ''

    try {
      const response = await fetchAuditLogsRequest({
        page: page.value,
        page_size: pageSize.value,
        action: filters.value.action,
        entity_type: filters.value.entity_type,
        organization_id: filters.value.organization_id,
        search: filters.value.search,
      }, options)

      if (token !== requestToken) {
        return { success: false, cancelled: true }
      }

      const items = extractArrayEnvelope(response, { keys: ['items', 'results', 'data'] })
      const pagination = parsePaginationEnvelope(response, {
        page: page.value,
        pageSize: pageSize.value,
        total: items.length,
      })
      const hasServerPagination = pagination.hasServerPagination

      backendPaginationAvailable.value = hasServerPagination

      if (hasServerPagination) {
        logs.value = items
        total.value = pagination.total
        page.value = pagination.page
        pageSize.value = pagination.pageSize
      } else {
        // Fallback when backend returns a plain list without pagination envelope.
        const normalizedPage = Math.max(1, toPositiveIntOr(page.value, DEFAULT_PAGE))
        const normalizedPageSize = Math.max(1, toPositiveIntOr(pageSize.value, DEFAULT_PAGE_SIZE))
        const start = (normalizedPage - 1) * normalizedPageSize
        const end = start + normalizedPageSize
        total.value = items.length
        logs.value = items.slice(start, end)
        degradedWarning.value = 'Backend pagination unavailable. Showing client-side fallback page.'
        if (end < total.value) {
          partialDataWarning.value = 'Only a slice is shown. Refine filters for more precise operational review.'
        }
      }
      lastSuccessfulFetchAt.value = new Date().toISOString()

      return { success: true, data: response }
    } catch (err) {
      if (token !== requestToken) {
        return { success: false, cancelled: true }
      }
      const detail = err?.response?.data?.detail
      const status = Number(err?.response?.status || 0)
      error.value = typeof detail === 'string' && detail.trim()
        ? detail
        : 'Failed to load audit logs'
      if (status === 408 || status === 504) {
        degradedWarning.value = 'Audit backend timed out. Retry to verify operational consistency.'
      }
      return { success: false, error: error.value }
    } finally {
      if (token === requestToken) {
        loading.value = false
      }
    }
  }

  const setPage = async (nextPage) => {
    const normalizedPage = Number(nextPage)
    page.value = Number.isInteger(normalizedPage) && normalizedPage > 0 ? normalizedPage : DEFAULT_PAGE
    return fetchAuditLogs()
  }

  const setPageSize = async (nextPageSize) => {
    const normalizedPageSize = Number(nextPageSize)
    pageSize.value = Number.isInteger(normalizedPageSize) && normalizedPageSize > 0 ? normalizedPageSize : DEFAULT_PAGE_SIZE
    page.value = DEFAULT_PAGE
    return fetchAuditLogs()
  }

  const setFilters = async (nextFilters = {}) => {
    filters.value = {
      ...filters.value,
      ...nextFilters,
    }
    page.value = DEFAULT_PAGE
    return fetchAuditLogs()
  }

  const clearFilters = async () => {
    filters.value = { ...DEFAULT_FILTERS }
    page.value = DEFAULT_PAGE
    return fetchAuditLogs()
  }

  return {
    logs,
    loading,
    error,
    page,
    pageSize,
    total,
    filters,
    lastSuccessfulFetchAt,
    degradedWarning,
    partialDataWarning,
    backendPaginationAvailable,
    fetchAuditLogs,
    setPage,
    setPageSize,
    setFilters,
    clearFilters,
  }
})

